import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Navigate, useNavigate, useSearchParams} from 'react-router-dom';
import {useInfiniteQuery, useQuery, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import {SmallTypography, StandardTypography} from 'patient-ping-remedy/packages/typography';
import Badge from 'patient-ping-remedy/packages/badge';
import Button from 'patient-ping-remedy/packages/button';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import {unionBy} from 'lodash-es';
import {differenceInCalendarDays, subDays} from 'date-fns';
import {FeatureToggle} from '@/api/dto/dto';
import {FilterResponse, FilterView} from '@/api/dto/filters';
import WorkSummary from "@/components/pages/tasks/WorkSummary";
import {
  AttestationStatus,
  attestationStatusDisplayValues,
  CareJourneyEventType,
  CareJourneyPriority,
  CareJourneyResponse,
  DaysOpenValue,
  formatTaskFiltersForApi,
  taskStatusDisplayValues,
  TaskStepName,
  taskTypeDisplayValues
} from '@/api/dto/care-journey';
import {SearchParam} from '@/helpers/search_params';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {useActiveRoster, useRosterStore} from '@/store/roster_store';
import {useStateConfig} from '@/hooks/useStateConfig';
import SortableTable, {SORT_ORDER, SortableTableHandle, TableColumn, TableData} from '@common/SortableTable';
import {TableLink} from '@common/TableLink.styles';
import AwsCommunicationsDrawer from '@/components/pages/tasks/aws_communications/AwsCommunicationsDrawer';
import Wrapper from '@common/pages/Wrapper';
import Helpers from '@/helpers/helpers';
import DisplayHelpers from '@/helpers/display_helpers';
import AssignedToTask from "@/components/pages/tasks/AssignedToTask";
import {dateRange, FilterInvalidate, nonGroupedValues} from "@common/FilterComponent";
import {
  MixpanelClientListEvent,
  MixpanelEvent,
  MixpanelEventType,
  MixpanelFilterEvent,
} from "@/api/dto/mixpanel";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import {useTaskStepName} from "@/hooks/useTaskStepName";
import {useMixpanel} from "@/hooks/useMixpanel";
import {useFilterQuery} from "@/hooks/useFilterQuery";
import {useSearchQuery} from "@/hooks/useSearchQuery";
import {SortOrderType} from "@clients/ClientsPage";

const tableHeaders = [
  "Name",
  "Date of Birth",
  "Event type",
  "Event Setting",
  "Days Open",
  "Group / Org",
  "Task type",
  "Status",
  "Next Step",
  "Priority",
  "Last Viewed",
  "Assigned to"
];

const priorityOptions: nonGroupedValues[] = [
  {label: 'Urgent', value: CareJourneyPriority.URGENT, id: CareJourneyPriority.URGENT},
  {label: 'None', value: 'NONE', id: 'NONE'},
];

const statusOptions: nonGroupedValues[] = [
  {label: 'In progress', value: 'active', id: 'active'},
  {label: 'Completed', value: 'completed', id: 'completed'},
  {label: 'Closed', value: 'closed', id: 'closed'},
];

const eventTypeOptions: nonGroupedValues[] = [
  {label: 'ADT event', value: 'ADT', id: 'ADT'},
];

const eventSettingOptions: nonGroupedValues[] = [
  {label: 'Inpatient', value: 'Inpatient', id: 'Inpatient'},
  {label: 'Emergency', value: 'Emergency', id: 'Emergency'},
  {label: 'Observation', value: 'Observation', id: 'Observation'},
  {label: 'Outpatient', value: 'Outpatient', id: 'Outpatient'},
  {label: 'Outpatient Surgery', value: 'Outpatient Surgery', id: 'Outpatient Surgery'},
  {label: 'Unknown', value: 'Unknown', id: 'Unknown'},
];


const daysOpenOptions: nonGroupedValues[] = [
  {label: '1 day', value: DaysOpenValue.OneDay, id: DaysOpenValue.OneDay},
  {label: '2-3 days', value: DaysOpenValue.TwoToThreeDays, id: DaysOpenValue.TwoToThreeDays},
  {label: '4-5 days', value: DaysOpenValue.FourToFiveDays, id: DaysOpenValue.FourToFiveDays},
  {label: '6-7 days', value: DaysOpenValue.SixToSevenDays, id: DaysOpenValue.SixToSevenDays},
  {label: 'More than 1 week', value: DaysOpenValue.MoreThanOneWeek, id: DaysOpenValue.MoreThanOneWeek},
  {label: 'More than 2 weeks', value: DaysOpenValue.MoreThanTwoWeeks, id: DaysOpenValue.MoreThanTwoWeeks},
  {label: 'More than 1 month', value: DaysOpenValue.MoreThanOneMonth, id: DaysOpenValue.MoreThanOneMonth},
  {label: 'More than 3 months', value: DaysOpenValue.MoreThanThreeMonths, id: DaysOpenValue.MoreThanThreeMonths},
  {label: 'More than 6 months', value: DaysOpenValue.MoreThanSixMonths, id: DaysOpenValue.MoreThanSixMonths},
  {label: 'More than 1 year', value: DaysOpenValue.MoreThanOneYear, id: DaysOpenValue.MoreThanOneYear},
];

// FLOWABLE_WORKFLOW is a temporary event type until workflows (and event types) are defined by product
// https://hbuco.atlassian.net/browse/CARECO-4605
const taskTypeOptions: nonGroupedValues[] = Object.values(CareJourneyEventType)
  .filter(eventType => eventType !== CareJourneyEventType.FLOWABLE_WORKFLOW)
  .map(eventType => ({ label: taskTypeDisplayValues[eventType], value: eventType, id: eventType }));

const attestationStatusOptions: nonGroupedValues[] = [
  {
    label: attestationStatusDisplayValues[AttestationStatus.NOT_READY],
    value: AttestationStatus.NOT_READY,
    id: AttestationStatus.NOT_READY
  },
  {
    label: attestationStatusDisplayValues[AttestationStatus.PENDING],
    value: AttestationStatus.PENDING,
    id: AttestationStatus.PENDING
  },
  {
    label: attestationStatusDisplayValues[AttestationStatus.COMPLETED],
    value: AttestationStatus.COMPLETED,
    id: AttestationStatus.COMPLETED
  },
  {
    label: attestationStatusDisplayValues[AttestationStatus.NOT_REQUIRED],
    value: AttestationStatus.NOT_REQUIRED,
    id: AttestationStatus.NOT_REQUIRED
  },
];

const daysSinceDischargeOptions: nonGroupedValues[] = [
  {label: '0 days', value: '0', id: '0'},
  ...Array.from({length: 30}, (_, i) => {
    const dayValue: number = i + 1;
    return {
      label: `${dayValue} ${dayValue === 1 ? 'day' : 'days'}`,
      value: dayValue.toString(),
      id: dayValue.toString(),
    };
  }),
];

export function formatLastViewed(lastViewedAt: string | null): string {
  if (!lastViewedAt) return '';
  const days = differenceInCalendarDays(new Date(), new Date(lastViewedAt));
  return days < 1 ? '<1 Day' : `${days} ${days === 1 ? 'Day ago' : 'Days ago'}`;
}

export enum TasksSearchBy {
  NAME = 'name',
  TASK_ID = 'taskId',
}

const searchByLabels: Record<TasksSearchBy, string> = {
  [TasksSearchBy.NAME]: 'Search by client name',
  [TasksSearchBy.TASK_ID]: 'Search by task ID',
};

const searchByDropdownLabels: Record<TasksSearchBy, string> = {
  [TasksSearchBy.NAME]: 'Client name',
  [TasksSearchBy.TASK_ID]: 'Task ID',
};

export default function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const oldTaskId = searchParams.get(SearchParam.TASK_ID);

  const {carecoApi} = useContext(CarecoApiContext);
  const stateConfig = useStateConfig();
  const {currentUser} = useAuthStore();
  const navigate = useNavigate();
  const [careJourneys, setCareJourneys] = useState<CareJourneyResponse[]>([]);
  const tableRef = useRef<SortableTableHandle>(null);
  const {taskStepDisplayValues, isLoadingTaskStepNames} = useTaskStepName();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const {checkBooleanToggle} = useFeatureToggles();
  const activeRoster = useActiveRoster();
  const selectedRoster = useRosterStore((state) => state.selectedRoster);


  const rosterTotalRef = useRef<number>(0);
  const searchTotalRef = useRef<number>(0);
  const sortKeyRef = useRef<string | undefined>('eventType');
  const sortOrderRef = useRef<SortOrderType | undefined>(SORT_ORDER.DESC);
  const queryClient = useQueryClient();

  const [currentSearchBy, setCurrentSearchBy] = useState(() => {
    return localStorage.getItem('search_by_tasks_page') || TasksSearchBy.NAME;
  });
  const [searchTermLength, setSearchTermLength] = useState(0);

  const awsCommunications = searchParams.get(SearchParam.AWS_COMMUNICATIONS);

  const {data: assignedToOptions} = useQuery({
    queryKey: ['assignedToOptions'],
    queryFn: () => carecoApi?.getCareJourneyAssignmentOptions(),
    enabled: !!carecoApi,
  });

  const {data: groupOptions} = useQuery({
    queryKey: ['groupOptions'],
    queryFn: () => carecoApi?.getTaskRosters(),
    enabled: !!carecoApi,
  });

  const {data: rosters} = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi,
  });

  const {data: careJourneysData, fetchNextPage, isLoading, error, isFetching} = useInfiniteQuery({
    queryKey: ['careJourneysData'],
    queryFn: ({pageParam}) => {
      const isTaskIdSearch = currentSearchBy === TasksSearchBy.TASK_ID;
      const suppressTaskIdSearch = searchTermLength === 4 && searchTerm.length < searchTermLength;
      const searchValue = queryFnSearch();
      return carecoApi?.getCareJourneys(pageParam, {
        sortBy: sortKeyRef.current,
        sortOrder: sortOrderRef.current?.value,
        ...formatTaskFiltersForApi(params),
        searchName: !isTaskIdSearch ? searchValue : null,
        searchTaskId: isTaskIdSearch && !suppressTaskIdSearch ? searchValue : null,
      });
    },
    enabled: !!carecoApi,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
  });

  const invalidate = useCallback<FilterInvalidate>(() => {
    void queryClient.removeQueries({queryKey: ['careJourneysData']});
    setCareJourneys([]);
  }, [queryClient]);

  const {params, queryFnCallback} = useFilterQuery({
    invalidate,
    params: {
      statuses: [statusOptions[0].value],
      priorities: [],
      eventTypes: [],
      daysOpen: [],
      daysSinceDischarge: [],
      taskDate: {} as dateRange,
      taskTypes: [],
      dueDate: {} as dateRange,
      nextSteps: [],
      lastViewed: {} as dateRange,
      assignees: [],
      attestationStatuses: [],
      groups: [],
    },
    multipleRoutesRendered: false,
    invalidateOnUnMount: false,
  });

  const appliedFiltersFromCache = queryClient.getQueryData<{
    filters: Record<string, unknown>;
  }>(['appliedTaskFilters']);

  const filterMountedRef = useRef(false);
  useEffect(() => {
    if (!filterMountedRef.current) {
      filterMountedRef.current = true;
      return;
    }
    if (params) {
      const hasFilters = Object.values(params).some(val => {
        if (Array.isArray(val)) return val.length > 0;
        if (val && typeof val === 'object')
          return Object.keys(val as unknown as Record<string, unknown>).length > 0;
        return !!val;
      });
      if (hasFilters) {
        queryClient.setQueryData(['appliedTaskFilters'], { filters: params });
      } else {
        queryClient.setQueryData(['appliedTaskFilters'], null);
      }
    }
  }, [params, queryClient]);

  const getCachedFilters = () => {
    if (!appliedFiltersFromCache?.filters) {
      return undefined;
    }
    const cached = appliedFiltersFromCache.filters;
    const transformed: Record<string, unknown> = {};

    if (Array.isArray(cached.statuses) && cached.statuses.length > 0) {
      transformed.statuses = statusOptions.filter(o =>
        (cached.statuses as string[]).includes(o.value));
    }
    if (Array.isArray(cached.priorities) && cached.priorities.length > 0) {
      transformed.priorities = priorityOptions.filter(o =>
        (cached.priorities as string[]).includes(o.value));
    }
    if (Array.isArray(cached.eventTypes) && cached.eventTypes.length > 0) {
      transformed.eventTypes = eventTypeOptions.filter(o =>
        (cached.eventTypes as string[]).includes(o.value));
    }
    if (Array.isArray(cached.eventSetting) && cached.eventSetting.length > 0) {
      transformed.eventSetting = eventSettingOptions.filter(o =>
        (cached.eventSetting as string[]).includes(o.value));
    }
    if (Array.isArray(cached.daysOpen) && cached.daysOpen.length > 0) {
      transformed.daysOpen = daysOpenOptions.filter(o =>
        (cached.daysOpen as string[]).includes(o.value));
    }
    if (Array.isArray(cached.taskTypes) && cached.taskTypes.length > 0) {
      transformed.taskTypes = taskTypeOptions.filter(o =>
        (cached.taskTypes as string[]).includes(o.value));
    }
    if (Array.isArray(cached.lastCompletedSteps)
      && cached.lastCompletedSteps.length > 0) {
      transformed.lastCompletedSteps = Object.entries(taskStepDisplayValues)
        .filter(([value]) =>
          (cached.lastCompletedSteps as string[]).includes(value))
        .map(([value, label]) => ({id: value, value, label}));
    }
    if (Array.isArray(cached.assignees) && cached.assignees.length > 0) {
      transformed.assignees = (assignedToOptions ?? []).filter(o =>
        (cached.assignees as string[]).includes(o.value));
    }
    if (Array.isArray(cached.attestationStatuses) && cached.attestationStatuses.length > 0) {
      transformed.attestationStatuses = attestationStatusOptions.filter(o =>
        (cached.attestationStatuses as string[]).includes(o.value));
    }
    if (cached.taskDate && typeof cached.taskDate === 'object'
      && Object.keys(cached.taskDate).length > 0) {
      transformed.taskDate = cached.taskDate;
    }
    if (cached.dueDate && typeof cached.dueDate === 'object'
      && Object.keys(cached.dueDate).length > 0) {
      transformed.dueDate = cached.dueDate;
    }
    if (Array.isArray(cached.groups) && cached.groups.length > 0) {
      transformed.groups = (groupOptions ?? []).filter(o =>
        (cached.groups as string[]).includes(o.value));
    }

    return Object.keys(transformed).length > 0 ? transformed : undefined;
  };

  const {searchTerm, searchFunction, queryFnSearch} = useSearchQuery({
    invalidate,
    queryFnCallback,
    canInvalidate,
  });

  function canInvalidate() {
    setSearchTermLength(searchTerm.length);
    return currentSearchBy === TasksSearchBy.NAME
      || (currentSearchBy === TasksSearchBy.TASK_ID
        && (searchTerm.length > 3 || searchTerm.length === 0))
      || (currentSearchBy === TasksSearchBy.TASK_ID
        && searchTermLength === 4 && searchTerm.length < searchTermLength);
  }

  const handleSearchByChange = (value: string) => {
    setCurrentSearchBy(value);
    localStorage.setItem('search_by_tasks_page', value);
  };

  useEffect(() => {
    if (careJourneysData) {
      const fetchedCareJourneys = careJourneysData.pages.flatMap(page => page?.data.careJourneys ?? []);
      rosterTotalRef.current = careJourneysData.pages[0]?.data.rosterTotal ?? 0;
      searchTotalRef.current = careJourneysData.pages[0]?.data.searchTotal ?? 0;
      setCareJourneys((prevCareJourneys : CareJourneyResponse[]) => prevCareJourneys ?
        unionBy(fetchedCareJourneys, prevCareJourneys, 'careJourneyInstanceId') : fetchedCareJourneys);
    }
  }, [careJourneysData]);

  const postMixpanelEvent = (response: FilterResponse) => {
    if (!response) return;

    const filterData = Helpers.safeJsonParse(response.data, {}) as Record<string, unknown>;
    const filterFieldsUsed = Helpers.expandFilterKeys(filterData as Record<string, unknown>);

    const event: MixpanelFilterEvent = {
      eventName: MixpanelEventType.BIH_PINNED_FILTER_APPLIED,
      filterId: response.id,
      filterName: response.name,
      filterOwnerId: response.ownerId,
      filterCriteriaCount: filterFieldsUsed.length,
      filterFieldsUsed,
      filterView: response.view,
      fieldsAdded: [],
      fieldsRemoved: [],
      ...mixpanelEventProperties!,
    };

    carecoApi?.postMixpanelEvent(event);
  };

  const handleNextPage = () => {
    if (careJourneysData?.pages.at(-1)?.data.careJourneys.length) {
      void fetchNextPage();
    }
  };

  const handleSortChange = (sortKey: TableColumn, sortOrder: SortOrderType) => {
    sortKeyRef.current = sortKey.key;
    sortOrderRef.current = sortOrder;
    invalidate();
  };

  const resetSort = () => {
    sortKeyRef.current = undefined;
    sortOrderRef.current = undefined;
  };

  const today = new Date();
  const getNumberOfDays = ((careJourney:CareJourneyResponse): number | null => {
    if (!careJourney.triggeringEventDateTime) {
      return null;
    }
    const triggerDate = new Date(careJourney.triggeringEventDateTime);
    if (isNaN(triggerDate.getTime())) {
      return null;
    }
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const triggerMidnight = new Date(triggerDate.getFullYear(), triggerDate.getMonth(), triggerDate.getDate());
    const diffTime = todayMidnight.getTime() - triggerMidnight.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  });

  const getEventType = useCallback((careJourney: CareJourneyResponse) => {
    return `${DisplayHelpers.readableEncounterType(careJourney.triggeringEventType, stateConfig)} event`;
  }, [stateConfig]);

  const tableData = useMemo(() => (careJourneys ?? []).map((careJourney): TableData => {
    const clientName = DisplayHelpers.formatName(careJourney.firstName, careJourney.middleName, careJourney.lastName);
    const taskDetailUrl = `/tasks/details?${SearchParam.TASK_ID}=${careJourney.careJourneyInstanceId}`
      + (careJourney.rosterId !== undefined ? `&${SearchParam.ROSTER_ID}=${careJourney.rosterId}` : '');

    const eventType = getEventType(careJourney);

    const dueDate = new Date(careJourney.dueDate);
    dueDate.setHours(0, 0, 0, 0);

    today.setHours(0, 0, 0, 0);

    const assignedToValues = careJourney.userAssignment.users?.map(user => user.id) || [];
    if (careJourney.userAssignment.assignedToAi) {
      assignedToValues.push("automated_agent");
    }

    const daysOpen = getNumberOfDays(careJourney);
    return {
      name: {
        tableHeader: 'Name',
        key: 'name',
        type: 'string',
        value: clientName,
        displayValue: <TableLink to={taskDetailUrl}>{clientName}</TableLink>,
        columnWidth: '15%',
      },
      dob: {
        tableHeader: 'Date of Birth',
        key: 'dob',
        type: 'date',
        value: careJourney.dateOfBirth,
        displayValue: DisplayHelpers.formatDateOfBirthWithAge(careJourney.dateOfBirth),
        columnWidth: '10%',
      },
      eventType: {
        tableHeader: 'Event type',
        key: 'eventType',
        type: 'string',
        value: [careJourney.triggeringEventDateTime, eventType].filter(Boolean).join(' '),
        displayValue: (
          <>
            <StandardTypography color={colors.primary1}>{eventType}</StandardTypography>
            <div>
              <SmallTypography>{DisplayHelpers.formatDate(careJourney.triggeringEventDateTime)}</SmallTypography>
            </div>
          </>
        ),
        columnWidth: '10%',
      },
      eventSetting: {
        tableHeader: 'Event Setting',
        key: 'eventSetting',
        type: 'string',
        value: careJourney.eventSetting ?? '',
        displayValue: (
          <StandardTypography color={colors.primary1}>
            {careJourney.eventSetting ?? ''}
          </StandardTypography>
        ),
        columnWidth: '9%',
      },
      daysOpen: {
        tableHeader: 'Days Open',
        key: 'daysOpen',
        type: 'number',
        value: daysOpen ?? '',
        displayValue: (
          <>
            <StandardTypography color={colors.primary1}>
              {daysOpen ?? '-'}
            </StandardTypography>
          </>
        ),
        columnWidth: '5%',
      },
      groupOrg: {
        tableHeader: 'Group / Org',
        key: 'groupOrg',
        type: 'string',
        value: [careJourney.groupName, careJourney.organizationName].filter(Boolean).join(' / '),
        displayValue: (
          <>
            <StandardTypography color={colors.primary1}>
              {careJourney.groupName}
            </StandardTypography>
            {careJourney.organizationName && (
              <div>
                <SmallTypography>{careJourney.organizationName}</SmallTypography>
              </div>
            )}
          </>
        ),
        columnWidth: '12%',
      },
      taskDate: {
        tableHeader: '',
        key: 'taskDate',
        type: 'date',
        value: careJourney.triggeringEventDateTime,
        displayValue: '',
        columnWidth: '0%'
      },
      taskType: {
        tableHeader: 'Task type',
        key: 'taskType',
        type: 'string',
        value: careJourney.eventType,
        displayValue: taskTypeDisplayValues[careJourney.eventType],
        columnWidth: '20%',
      },
      status: {
        tableHeader: 'Status',
        key: 'status',
        type: 'string',
        value: careJourney.status ? taskStatusDisplayValues[careJourney.status] ?? '' : '',
        displayValue: (
          <>
            <StandardTypography color={colors.primary1}>
              {careJourney.status ? taskStatusDisplayValues[careJourney.status] : ''}
            </StandardTypography>
            {!isNaN(dueDate.getTime()) && (
              <div>
                {dueDate >= today
                  ? <SmallTypography>Due {DisplayHelpers.formatDate(dueDate)}</SmallTypography>
                  : <SmallTypography color={colors.red5}>Overdue</SmallTypography>}
              </div>
            )}
          </>
        ),
        columnWidth: '10%',
      },
      dueDate: {
        tableHeader: '',
        key: 'dueDate',
        type: 'date',
        value: DisplayHelpers.formatDate(dueDate) ?? " ",
        displayValue: '',
        columnWidth: '0%',
      },
      nextStep: {
        tableHeader: 'Next Step',
        key: 'nextStep',
        type: 'string',
        value: careJourney.nextStep ?? "",
        displayValue: (
          <>
            <StandardTypography color={colors.primary1}>
              {taskStepDisplayValues[careJourney.nextStep as TaskStepName] ?? careJourney.nextStep}
            </StandardTypography>
          </>
        ),
        columnWidth: '10%',
      },
      priority: {
        tableHeader: 'Priority',
        key: 'priority',
        type: 'string',
        value: careJourney.priority ?? 'none',
        displayValue: careJourney.priority && (
          <Badge
            backgroundColor={careJourney.priority === CareJourneyPriority.URGENT ? colors.red1 : colors.gray1}
            textColor={careJourney.priority === CareJourneyPriority.URGENT ? colors.red5 : colors.charcoal}
          >
            {DisplayHelpers.humanize(careJourney.priority)}
          </Badge>
        ),
        columnWidth: '8%',
      },
      lastViewed: {
        tableHeader: 'Last Viewed',
        key: 'lastViewed',
        type: 'string',
        value: careJourney.lastViewedAt ?? '',
        displayValue: (
          <StandardTypography color={colors.primary1}>
            {formatLastViewed(careJourney.lastViewedAt)}
          </StandardTypography>
        ),
        columnWidth: '8%',
      },
      assignedTo: {
        tableHeader: 'Assigned to',
        key: 'assignedTo',
        type: 'custom',
        value: assignedToValues,
        displayValue: careJourney ?
          <AssignedToTask
            careJourney={careJourney}
            roster={rosters?.data?.data?.find(r => r.id === careJourney.rosterId)}
            showAiAssignment={careJourney.journeySource !== 'FLOWABLE'}
          /> : "",
        columnWidth: "12%"
      }
    };
  }), [careJourneys]);

  useEffect(() => {
    if (mixpanelEventProperties) {
      const event: MixpanelEvent = {
        eventName: MixpanelEventType.BIH_TASKS_PAGE_VIEWED,
        ...mixpanelEventProperties,
      };
      carecoApi?.postMixpanelEvent(event);
    }
  }, [mixpanelEventProperties]);

  // Redirect old task detail links (/tasks?t=<id>&r=<rosterId>) to new route
  if (oldTaskId) {
    return <Navigate to={`/tasks/details?${searchParams}`} replace />;
  }

  const closeAwsCommunicationsDrawer = () => {
    if (searchParams.has(SearchParam.AWS_COMMUNICATIONS)) {
      searchParams.delete(SearchParam.AWS_COMMUNICATIONS);
      setSearchParams(searchParams);
    }
  };

  return (
    <Wrapper>
      <>
        <WorkSummary
          onAssignedClick={(currentUserUuid) => {
            tableRef.current?.applyFilter({
              statuses: [statusOptions[0]],
              assignees: [{label: currentUser?.first_name ?? '', value: currentUserUuid, id: currentUserUuid}],
            });
          }}
          onOverdueClick={(currentUserUuid) => {
            tableRef.current?.applyFilter({
              statuses: [statusOptions[0]],
              assignees: [{label: currentUser?.first_name ?? '', value: currentUserUuid, id: currentUserUuid}],
              dueDate: {startDate: null, endDate: subDays(new Date(), 1)},
            });
          }}
          onPinnedFilterClick={(filter) => {
            tableRef.current?.applyFilter(filter);
            postMixpanelEvent(filter);
          }}
        />
        <SortableTable
          id={FilterView.TASKS_TABLE}
          loading={isLoading || isLoadingTaskStepNames}
          fetching={isFetching}
          data={tableData}
          loadError={error}
          header={tableHeaders}
          sortableColumns={tableData[0] ? [
            tableData[0]['eventType'],
            tableData[0]['daysOpen'],
            tableData[0]['groupOrg'],
            tableData[0]['taskType'],
            tableData[0]['status'],
            tableData[0]['nextStep'],
            tableData[0]['priority'],
          ] : []}
          filterOptions={[
            {
              type: "multiselect-search",
              key: "eventTypes",
              options: eventTypeOptions,
              label: "Event type",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "multiselect-search",
              key: "eventSettings",
              options: eventSettingOptions,
              label: "Event Setting",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "multiselect-search",
              key: "daysOpen",
              options: daysOpenOptions,
              label: "Days Open",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "single-select",
              key: "daysSinceDischarge",
              options: daysSinceDischargeOptions,
              label: "Days Since Discharge",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "date-range",
              key: "taskDate",
              label: "Task Start Date",
              endLabel: "Task End Date",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "multiselect-search",
              key: "taskTypes",
              options: taskTypeOptions,
              label: "Task type",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "multiselect-search",
              key: "statuses",
              options: statusOptions,
              label: "Status",
              dataType: 'ajax',
              query: {params, invalidate}
            },
            {
              type: "date-range",
              key: "dueDate",
              label: "Due Start Date",
              endLabel: "Due End Date",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "multiselect-search",
              key: "nextSteps",
              options: Object.entries(taskStepDisplayValues).map(([value, label]) => ({
                id: value,
                value,
                label,
              })),
              label: "Next Step",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "multiselect-search",
              key: "priorities",
              options: priorityOptions,
              label: "Priority",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "date-range",
              key: "lastViewed",
              label: "Last Viewed Start Date",
              endLabel: "Last Viewed End Date",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "multiselect-search",
              key: "assignees",
              options: assignedToOptions ?? [],
              label: "Assignee",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "multiselect-search",
              key: "attestationStatuses",
              options: attestationStatusOptions,
              label: "Attestation Status",
              dataType: "ajax",
              query: {params, invalidate}
            },
            {
              type: "multiselect-search",
              key: "groups",
              options: (groupOptions ?? []).map(o => ({label: o.label, value: o.value, id: o.value})),
              label: "Group",
              dataType: "ajax",
              query: {params, invalidate}
            }
          ]}
          pinnedFilters
          searchColumns={['name']}
          searchPlaceholder={
            currentSearchBy === TasksSearchBy.TASK_ID
              ? searchByLabels[TasksSearchBy.TASK_ID]
              : searchByLabels[TasksSearchBy.NAME]
          }
          searchBy={[
            {label: searchByLabels[TasksSearchBy.NAME], value: TasksSearchBy.NAME},
            {label: searchByLabels[TasksSearchBy.TASK_ID], value: TasksSearchBy.TASK_ID},
          ]}
          handleSearchByChange={handleSearchByChange}
          searchDropdownPlaceholder={
            currentSearchBy === TasksSearchBy.TASK_ID
              ? searchByDropdownLabels[TasksSearchBy.TASK_ID]
              : searchByDropdownLabels[TasksSearchBy.NAME]
          }
          tableOverrides={{
            initialFilters: {
              statuses: [statusOptions[0]]
            },
            currentFiltersOverride: !filterMountedRef.current && !isLoadingTaskStepNames && assignedToOptions
              ? getCachedFilters() : undefined,
            initialSort: tableData[0] ? {
              tableColumn: tableData[0].eventType,
              sortOrder: SORT_ORDER.DESC
            } : null,
            count: rosterTotalRef.current,
            filteredCount: searchTotalRef.current,
            searchTerm,
            searchFunction,
            resetSortFunction: resetSort,
            sortFunction: handleSortChange,
            pinnedLabel: "Pin view to 'Your Work Summary'"
          }}
          onNextPage={handleNextPage}
          ref={tableRef}
        >
          {checkBooleanToggle(FeatureToggle.CARECO_3810_AWS_COMMUNICATIONS) && (
            <Button
              onClick={() => {
                if (mixpanelEventProperties) {
                  // Attribute to the group the panel will show, resolved the same way as AwsCommunicationsDrawer.
                  // With no group selected the panel shows its empty state, so the event carries no group.
                  const panelRoster = activeRoster ?? selectedRoster;
                  const event: MixpanelClientListEvent = {
                    eventName: MixpanelEventType.BIH_AWS_COMMUNICATIONS_OPENED,
                    ...mixpanelEventProperties,
                    ...(panelRoster ? getCurrentGroupDetails(panelRoster) : {})
                  };
                  carecoApi?.postMixpanelEvent(event);
                }
                navigate(`/tasks?${SearchParam.AWS_COMMUNICATIONS}=true`);
              }}
            >
              <Icon
                iconClass={ICONS['address-book']}
                weight="far"
                className={css({ marginRight: sizes.xSmall })}
              />
                    AWS Communications
            </Button>
          )}
        </SortableTable>
      </>
      <AwsCommunicationsDrawer
        isOpen={!!awsCommunications}
        onClose={closeAwsCommunicationsDrawer}
      />
    </Wrapper>
  );
}
